import React from "react";
import databricksLogo from "../assets/images/logo-databricks.png";
import snowflakeLogo from "../assets/images/logo-snowflake.svg.png";

interface Column {
  name: string;
  description: string;
}

interface Dataset {
  title: string;
  description: string;
  columns: Column[];
  technicalDetails: string[];
  urlSnowflake: string;
  urlDatabricks: string;
  urlStripe?: string;
}

interface DatasetDetailPageProps {
  dataset: Dataset;
}

const DatasetDetailPage: React.FC<DatasetDetailPageProps> = ({ dataset }) => {
  const {
    title,
    description,
    columns,
    technicalDetails,
    urlSnowflake,
    urlDatabricks,
    urlStripe,
  } = dataset;

  // Stripe Checkout Popup
  const handleCheckout = () => {
    const width = 500;
    const height = 700;
    const left = (window.innerWidth - width) / 2;
    const top = (window.innerHeight - height) / 2;

    window.open(
      urlStripe,
      "_blank",
      `width=${width},height=${height},top=${top},left=${left},noopener`
    );
  };

  return (
    <div className="bg-background min-h-screen">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-r from-gradientFrom to-gradientTo text-white py-16 text-center px-6">
        <h1 className="text-5xl font-serif font-bold mb-4">{title}</h1>
        <p className="text-lg font-sans max-w-3xl mx-auto">{description}</p>
      </div>

      {/* Content Section */}
      <div className="container mx-auto p-10 bg-white rounded-lg shadow-lg mt-8">
        {/* Column Details */}
        <div className="mb-10">
          <h2 className="text-2xl font-serif text-primary mb-6">
            Column Details:
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {columns.map((column, index) => (
              <div key={index} className="p-4 bg-gray-100 rounded-lg shadow">
                <h3 className="text-lg font-semibold text-primary">
                  {column.name}
                </h3>
                <p className="text-primary/70">{column.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Technical Details */}
        <div className="mt-20 mb-10">
          <h2 className="text-2xl font-serif text-primary mb-6">
            Cloud-Based Technical Details:
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {technicalDetails.map((detail, index) => (
              <div key={index} className="p-4 bg-gray-100 rounded-lg shadow">
                <p className="text-primary/70">{detail}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Links to Snowflake & Databricks */}
        <div className="flex justify-center items-center space-x-8 mb-10">
          <a href={urlSnowflake} target="_blank" rel="noopener noreferrer">
            <img
              src={snowflakeLogo}
              alt="Snowflake"
              className="h-12 transition-transform transform hover:scale-110"
            />
          </a>
          <a href={urlDatabricks} target="_blank" rel="noopener noreferrer">
            <img
              src={databricksLogo}
              alt="Databricks"
              className="h-12 transition-transform transform hover:scale-110"
            />
          </a>
        </div>

        {/* Checkout Button */}
        {urlStripe && (
          <div className="text-center mt-8">
            <button
              onClick={handleCheckout}
              className="bg-[#EE592F] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#D04A28] transition duration-200"
            >
              Subscribe Now
            </button>
          </div>
        )}

        {/* Footer Text */}
        <p className="text-sm font-sans text-primary/50 mt-10 text-center">
          For a full list of included tickers and symbols, please visit{" "}
          <a
            href="/stock-list"
            target="_blank"
            className="text-blue-500 underline hover:text-blue-700"
          >
            here
          </a>
          .
        </p>
        <p className="text-sm font-sans text-primary/50 text-center">
          Customized data requests, including increased frequency and historical
          data, are available. For additional specifications, please reach out
          to{" "}
          <a
            href="mailto:letstalk@openmetadata.net"
            className="font-bold hover:underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            letstalk@openmetadata.net
          </a>
        </p>
      </div>
    </div>
  );
};

export default DatasetDetailPage;
